(() => {
  if (window.ARIIntentSurveyLoaded) return;
  window.ARIIntentSurveyLoaded = true;

  const STORAGE_KEY = 'ari_intent_survey_v1';
  const DAY_MS = 24 * 60 * 60 * 1000;

  const readState = () => {
    try {
      const state = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
      if (
        !state ||
        typeof state !== 'object' ||
        Array.isArray(state) ||
        !Number.isFinite(state.expiresAt) ||
        state.expiresAt <= Date.now()
      ) {
        return null;
      }
      return state;
    } catch (_) {
      return null;
    }
  };

  const writeState = (status, genres, vocalTypes, duration) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        status,
        genres,
        vocalTypes,
        timestamp: Date.now(),
        expiresAt: Date.now() + duration,
      }));
    } catch (_) {
      // In-memory eligibility still prevents a second opening this page view.
    }
  };

  const setClarityTag = (key, value) => {
    if (typeof window.clarity === 'function') window.clarity('set', key, value);
  };

  const emit = (name, detail = {}) => {
    if (typeof window.ARIStorefront?.emit === 'function') {
      window.ARIStorefront.emit(name, detail);
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...detail });
    if (typeof window.clarity === 'function') window.clarity('event', name);
    document.dispatchEvent(new CustomEvent(name, { detail }));
  };

  const initIntentSurvey = (catalog) => {
    if (catalog.dataset.ariIntentReady === 'true') return;

    const rows = Array.from(catalog.querySelectorAll('[data-ari-product-row]'));
    if (rows.length === 0 || catalog.dataset.intentEnabled !== 'true') return;

    const store = catalog.closest('.ari-store');
    const survey = store?.querySelector('[data-ari-intent-survey]');
    const panel = survey?.querySelector('.ari-intent__panel');
    const genreStep = survey?.querySelector('[data-ari-intent-step="genre"]');
    const vocalTypeStep = survey?.querySelector('[data-ari-intent-step="vocal-type"]');
    const skipVocalType = survey?.querySelector('[data-ari-intent-skip-type]');
    const surveyStatus = survey?.querySelector('[data-ari-intent-status]');
    const resultSummary = catalog.querySelector('.ari-vocal-browser__summary');
    if (!survey || !panel || !genreStep || !vocalTypeStep) return;

    catalog.dataset.ariIntentReady = 'true';

    const state = {
      eligible: !readState(),
      opened: false,
      currentStep: 'genre',
      genres: [],
      vocalTypes: [],
      playedProductIds: new Set(),
      activePlaybackMs: 0,
      activeStartedAt: 0,
      triggerTimer: 0,
      returnFocus: null,
    };

    if (!state.eligible) return;

    const productIds = new Set(rows.map((row) => String(row.dataset.productId || '')).filter(Boolean));
    const catalogAudio = Array.from(catalog.querySelectorAll('[data-ari-audio-track] audio'));
    const triggerMs = Math.max(0, Number(catalog.dataset.intentTriggerSeconds) || 0) * 1000;
    let activeAudio = null;
    let submitting = false;

    const selectedInputs = (step) =>
      Array.from(step.querySelectorAll('[data-ari-intent-choice]:checked'));

    const selectedValues = (step) => selectedInputs(step).map((input) => input.value);

    const selectedLabels = (step) =>
      selectedInputs(step).map((input) => input.dataset.label || input.value);

    const updatePrimaryButton = (step) => {
      const primary = step.querySelector('.ari-intent__primary');
      if (primary) primary.disabled = selectedValues(step).length === 0;
    };

    const configureChoices = (step) => {
      const choices = Array.from(step.querySelectorAll('[data-ari-intent-choice]'));
      choices.forEach((choice) => {
        choice.addEventListener('change', () => {
          if (choice.checked && choice.hasAttribute('data-exclusive-choice')) {
            choices.forEach((sibling) => {
              if (sibling !== choice) sibling.checked = false;
            });
          } else if (choice.checked) {
            const exclusiveChoice = choices.find((sibling) => sibling.hasAttribute('data-exclusive-choice'));
            if (exclusiveChoice) exclusiveChoice.checked = false;
          }
          updatePrimaryButton(step);
        });
      });
      updatePrimaryButton(step);
    };

    const clearTriggerTimer = () => {
      if (!state.triggerTimer) return;
      window.clearTimeout(state.triggerTimer);
      state.triggerTimer = 0;
    };

    const cancelPlaybackTracking = () => {
      clearTriggerTimer();
      state.activeStartedAt = 0;
      activeAudio = null;
    };

    const hideSurvey = (focusTarget) => {
      survey.hidden = true;
      delete survey.dataset.open;
      document.documentElement.classList.remove('ari-intent-open');
      state.opened = false;
      if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
    };

    const closeSurvey = (reason, duration) => {
      if (!state.opened) return;
      state.eligible = false;
      cancelPlaybackTracking();
      writeState('dismissed', state.genres, state.vocalTypes, duration);
      setClarityTag('ari_intent_status', reason);
      emit('ari_intent_survey_skip', { reason, step: state.currentStep });
      hideSurvey(state.returnFocus);
    };

    const openSurvey = () => {
      if (!state.eligible || state.opened) return;
      state.eligible = false;
      state.opened = true;
      cancelPlaybackTracking();
      survey.hidden = false;
      survey.dataset.open = 'true';
      document.documentElement.classList.add('ari-intent-open');
      state.returnFocus = document.activeElement;
      panel.focus({ preventScroll: true });
      emit('ari_intent_survey_open', { distinct_previews: state.playedProductIds.size });
    };

    const pauseActivePlayback = (audio = activeAudio) => {
      if (audio && activeAudio && audio !== activeAudio) return;
      clearTriggerTimer();
      if (state.activeStartedAt) {
        state.activePlaybackMs += Math.max(0, Date.now() - state.activeStartedAt);
        state.activeStartedAt = 0;
      }
      if (state.eligible && state.playedProductIds.size >= 2 && state.activePlaybackMs >= triggerMs) {
        openSurvey();
      }
    };

    const armTriggerTimer = (audio) => {
      if (
        !state.eligible ||
        state.opened ||
        state.playedProductIds.size < 2 ||
        !audio ||
        audio.paused ||
        audio.ended ||
        document.hidden
      ) {
        return;
      }

      activeAudio = audio;
      clearTriggerTimer();
      const remaining = Math.max(0, triggerMs - state.activePlaybackMs);
      if (remaining === 0) {
        openSurvey();
        return;
      }

      state.activeStartedAt = Date.now();
      state.triggerTimer = window.setTimeout(() => {
        state.triggerTimer = 0;
        pauseActivePlayback();
        if (state.eligible && activeAudio && !activeAudio.paused && !activeAudio.ended && !document.hidden) {
          armTriggerTimer(activeAudio);
        }
      }, remaining);
    };

    const showStep = (stepName) => {
      state.currentStep = stepName;
      genreStep.hidden = stepName !== 'genre';
      vocalTypeStep.hidden = stepName !== 'vocal-type';
      updatePrimaryButton(stepName === 'genre' ? genreStep : vocalTypeStep);
      const firstChoice = (stepName === 'genre' ? genreStep : vocalTypeStep).querySelector(
        '[data-ari-intent-choice]',
      );
      firstChoice?.focus({ preventScroll: true });
    };

    const completeSubmission = (result, skippedStepTwo) => {
      const matching = Number(result.matching) || 0;
      const fallback = Boolean(result.fallback);
      const matchType = result.matchType || (fallback ? 'fallback' : 'exact');
      const intentStatus = skippedStepTwo ? 'skipped_step_2' : 'completed';

      if (surveyStatus) {
        surveyStatus.textContent = fallback
          ? 'No exact matches yet. Showing the current bestsellers.'
          : `${matching} matching vocal${matching === 1 ? '' : 's'} found.`;
      }

      setClarityTag('ari_vocal_type_intent', state.vocalTypes);
      setClarityTag('ari_intent_status', intentStatus);
      setClarityTag('ari_intent_match', matchType);
      emit('ari_intent_survey_submit', {
        genres: state.genres,
        genre_labels: selectedLabels(genreStep),
        vocal_types: state.vocalTypes,
        vocal_type_labels: skippedStepTwo ? [] : selectedLabels(vocalTypeStep),
        status: intentStatus,
        matching,
        fallback,
        match_type: matchType,
      });
      writeState('completed', state.genres, state.vocalTypes, 30 * DAY_MS);

      state.eligible = false;
      cancelPlaybackTracking();
      if (resultSummary) resultSummary.setAttribute('tabindex', '-1');
      hideSurvey(resultSummary);
    };

    const applyIntent = (skippedStepTwo) => {
      if (submitting) return;
      submitting = true;
      catalog.addEventListener(
        'ari:intent:result',
        (event) => completeSubmission(event.detail || {}, skippedStepTwo),
        { once: true },
      );
      catalog.dispatchEvent(new CustomEvent('ari:intent:apply', {
        detail: { genres: state.genres, vocalTypes: state.vocalTypes },
      }));
    };

    configureChoices(genreStep);
    configureChoices(vocalTypeStep);

    genreStep.addEventListener('submit', (event) => {
      event.preventDefault();
      state.genres = selectedValues(genreStep);
      if (state.genres.length === 0) return;
      setClarityTag('ari_genre_intent', state.genres);
      emit('ari_intent_genre_continue', { genres: state.genres });
      showStep('vocal-type');
    });

    vocalTypeStep.addEventListener('submit', (event) => {
      event.preventDefault();
      state.vocalTypes = selectedValues(vocalTypeStep);
      if (state.vocalTypes.length === 0) return;
      applyIntent(false);
    });

    skipVocalType?.addEventListener('click', () => {
      state.vocalTypes = [];
      setClarityTag('ari_intent_status', 'skipped_step_2');
      emit('ari_intent_survey_skip', { reason: 'step_2', step: 'vocal-type' });
      applyIntent(true);
    });

    survey.querySelectorAll('[data-ari-intent-dismiss]').forEach((control) => {
      control.addEventListener('click', () => closeSurvey('dismiss', 7 * DAY_MS));
    });

    panel.addEventListener('keydown', (event) => {
      if (!state.opened) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSurvey('escape', 7 * DAY_MS);
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      ).filter((element) => !element.closest('[hidden]'));
      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (
        event.shiftKey &&
        (document.activeElement === panel || document.activeElement === first || !panel.contains(document.activeElement))
      ) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    });

    document.addEventListener('ari_audio_play', (event) => {
      if (!state.eligible || !catalog.isConnected) return;
      const productId = String(event.detail?.product_id || '');
      if (!productIds.has(productId) || typeof window.CSS?.escape !== 'function') return;

      const audio = catalog.querySelector(
        `[data-ari-audio-track][data-product-id="${CSS.escape(productId)}"] audio`,
      );
      if (!audio || audio.paused || audio.ended) return;

      if (activeAudio && activeAudio !== audio) pauseActivePlayback();
      if (!state.eligible) return;
      activeAudio = audio;
      state.playedProductIds.add(productId);
      if (state.playedProductIds.size < 2) return;
      armTriggerTimer(audio);
    });

    catalogAudio.forEach((audio) => {
      audio.addEventListener('pause', () => pauseActivePlayback(audio));
      audio.addEventListener('ended', () => pauseActivePlayback(audio));
    });

    document.addEventListener('visibilitychange', () => {
      if (!state.eligible) return;
      if (document.hidden) pauseActivePlayback();
      else if (activeAudio && !activeAudio.paused && !activeAudio.ended) armTriggerTimer(activeAudio);
    });
    window.addEventListener('pagehide', () => pauseActivePlayback());
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-ari-catalog]').forEach(initIntentSurvey);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
